import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from 'src/base-component';
import { Breadcrumb, Card, Col, DatePicker, Row, Select, Table } from 'antd';
import { AnalyticsApi } from 'src/api';
import moment from 'moment';
import { Column, Line, Bar } from '@ant-design/charts';
import type { RangePickerProps } from 'antd/es/date-picker';

// Імпортуємо інтерфейси для аналітики замовлень
import type {
    IOrderStatusAnalytics,
    IMonthlyAnalytics,
    IManagerAnalytics,
    IOrderTypeAnalytics,
    IGeneralStats
} from 'src/api/AnalyticsApi';

const { RangePicker } = DatePicker;

export const AnalyticsOrdersPage: React.FC = () => {
    const [statusAnalytics, setStatusAnalytics] = useState<IOrderStatusAnalytics[]>([]);
    const [monthlyAnalytics, setMonthlyAnalytics] = useState<IMonthlyAnalytics[]>([]);
    const [managerAnalytics, setManagerAnalytics] = useState<IManagerAnalytics[]>([]);
    const [typeAnalytics, setTypeAnalytics] = useState<IOrderTypeAnalytics[]>([]);
    const [generalStats, setGeneralStats] = useState<IGeneralStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment] | null>(null);
    const [selectedYear, setSelectedYear] = useState<string>(moment().format('YYYY'));

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = dateRange ? {
                dateFrom: dateRange[0].format('YYYY-MM-DD'),
                dateTo: dateRange[1].format('YYYY-MM-DD')
            } : undefined;

            const [statusRes, monthlyRes, managerRes, typeRes, generalRes] = await Promise.all([
                AnalyticsApi.getOrderStatusAnalytics(params),
                AnalyticsApi.getMonthlyAnalytics({ year: selectedYear }),
                AnalyticsApi.getManagerAnalytics(params),
                AnalyticsApi.getOrderTypeAnalytics(params),
                AnalyticsApi.getGeneralStats(params)
            ]);

            if (statusRes?.data) setStatusAnalytics(statusRes.data);
            if (monthlyRes?.data) setMonthlyAnalytics(monthlyRes.data);
            if (managerRes?.data) setManagerAnalytics(managerRes.data);
            if (typeRes?.data) setTypeAnalytics(typeRes.data);
            if (generalRes?.data) setGeneralStats(generalRes.data);
        } catch (error) {
            console.error('Error loading analytics data:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange, selectedYear]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
        setDateRange(dates ? [moment(dates[0]?.toDate()), moment(dates[1]?.toDate())] : null);
    };

    const handleYearChange = (year: string): void => {
        setSelectedYear(year);
        setDateRange([moment(`${year}-01-01`), moment(`${year}-12-31`)]);
    };

    // Колонки для таблиць
    const statusColumns = [
        {
            title: 'Статус',
            dataIndex: 'status_name',
            key: 'status_name',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальна сума',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    const monthlyColumns = [
        {
            title: 'Місяць',
            dataIndex: 'month',
            key: 'month',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальна сума',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
        {
            title: 'Середня сума',
            dataIndex: 'avg_amount',
            key: 'avg_amount',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    const managerColumns = [
        {
            title: 'Менеджер',
            dataIndex: 'manager_name',
            key: 'manager_name',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальна сума',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
        {
            title: 'Середня сума',
            dataIndex: 'avg_amount',
            key: 'avg_amount',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    const typeColumns = [
        {
            title: 'Тип замовлення',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальна сума',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    return (
        <>
            <PageHeader
                title="Аналітика замовлень"
                subtitle="Детальна аналітика по замовленнях"
                breadcrumbs={
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <a href="/">Головна</a>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Аналітика</Breadcrumb.Item>
                        <Breadcrumb.Item>Замовлення</Breadcrumb.Item>
                    </Breadcrumb>
                }
                extra={[
                    <RangePicker
                        key="dateRange"
                        onChange={handleDateRangeChange}
                        placeholder={['Дата від', 'Дата до']}
                        style={{ marginRight: 16 }}
                    />,
                    <Select
                        key="year"
                        value={selectedYear}
                        onChange={handleYearChange}
                        style={{ width: 120 }}
                        placeholder="Рік"
                    >
                        {Array.from({ length: 5 }, (_, i) => moment().subtract(i, 'year').format('YYYY')).map(year => (
                            <Select.Option key={year} value={year}>{year}</Select.Option>
                        ))}
                    </Select>
                ]}
            />

            {/* Загальна статистика */}
            {generalStats && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                        <Card>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                                    {generalStats.total_orders}
                                </div>
                                <div>Всього замовлень</div>
                            </div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                    {generalStats.total_amount?.toLocaleString()} грн
                                </div>
                                <div>Загальна сума</div>
                            </div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                                    {generalStats.avg_order_amount?.toLocaleString()} грн
                                </div>
                                <div>Середній чек</div>
                            </div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                                    {generalStats.orders_in_progress}
                                </div>
                                <div>У роботі</div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Графіки та таблиці */}
            {/* Ряд 1: Розподіл по статусах */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Розподіл по статусах" loading={loading}>
                        <Column
                            data={statusAnalytics}
                            xField="status_name"
                            yField="count"
                            yAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} шт`,
                                },
                            }}
                            meta={{
                                count: {
                                    alias: 'Кількість',
                                },
                            }}
                            tooltip={{
                                title: 'Кількість замовлень',
                                formatter: (datum: IOrderStatusAnalytics) => ({
                                    name: 'Кількість',
                                    value: datum.count?.toLocaleString() + ' шт',
                                }),
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по статусах" loading={loading}>
                        <Table
                            dataSource={statusAnalytics}
                            columns={statusColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Ряд 2: Розподіл по типах замовлень */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Розподіл по типах замовлень" loading={loading}>
                        <Column
                            data={typeAnalytics}
                            xField="type"
                            yField="count"
                            yAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} шт`,
                                },
                            }}
                            meta={{
                                count: {
                                    alias: 'Кількість',
                                },
                            }}
                            tooltip={{
                                title: 'Кількість замовлень',
                                formatter: (datum: IOrderTypeAnalytics) => ({
                                    name: 'Кількість',
                                    value: datum.count?.toLocaleString() + ' шт',
                                }),
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по типах" loading={loading}>
                        <Table
                            dataSource={typeAnalytics}
                            columns={typeColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Ряд 3: Динаміка по місяцях */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Динаміка замовлень по місяцях" loading={loading}>
                        <Line
                            data={monthlyAnalytics}
                            xField="month"
                            yField="total_amount"
                            yAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} грн`,
                                },
                            }}
                            tooltip={{
                                title: 'Сума замовлень',
                                formatter: (datum: IMonthlyAnalytics) => ({
                                    name: 'Сума',
                                    value: datum.total_amount?.toLocaleString() + ' грн',
                                }),
                            }}
                            point={{
                                size: 5,
                                shape: 'diamond',
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по місяцях" loading={loading}>
                        <Table
                            dataSource={monthlyAnalytics}
                            columns={monthlyColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Ряд 4: Ефективність менеджерів */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Ефективність менеджерів" loading={loading}>
                        <Bar
                            data={managerAnalytics}
                            xField="total_amount"
                            yField="manager_name"
                            xAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} грн`,
                                },
                            }}
                            tooltip={{
                                title: 'Сума замовлень',
                                formatter: (datum: IManagerAnalytics) => ({
                                    name: 'Сума',
                                    value: datum.total_amount?.toLocaleString() + ' грн',
                                }),
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по менеджерах" loading={loading}>
                        <Table
                            dataSource={managerAnalytics}
                            columns={managerColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </>
    );
};
